import { getDistanceGCS } from "@utils/coords";
import type { HubMapQuery$data } from "./__generated__/HubMapQuery.graphql";

type HubListItem = HubMapQuery$data["hubs"]["nodes"][number];

const HUB_FILTER_SERVICES = [
	"ONEDELIVERY",
	"INSTANT_BUY",
	"SHOP_N_COLLECT",
	"CLICK_N_COLLECT",
	"RAFFLE",
	"LOCK_B2B",
	"LOCK_B2C",
] as const satisfies ReadonlyArray<HubListItem["meta"]["services"][number]>;

export type HubFilterService = (typeof HUB_FILTER_SERVICES)[number];

type Coordinates = {
	latitude: number;
	longitude: number;
};

export type HubListItemWithDistance = HubListItem & {
	distance?: number;
};

type FilterableHub = Pick<HubListItem, "meta" | "sncProducts">;

type HubFilterOptions = {
	filterService?: HubFilterService | null;
	selectedCity?: string | null;
};

const ignoredHubNames = new Set(["tuv", "vilo"]);

export const matchesHubMapFilters = (hub: FilterableHub, options: HubFilterOptions) => {
	const { filterService, selectedCity } = options;

	// The Relay store can hold hub records that other queries loaded with only a
	// few meta fields; skip them until their name is available.
	const name = hub.meta?.name as string | null | undefined;
	if (!name || ignoredHubNames.has(name.trim().toLowerCase())) {
		return false;
	}

	if (selectedCity && hub.meta.city?.trim() !== selectedCity) {
		return false;
	}

	if (filterService === "SHOP_N_COLLECT") {
		// SHOP_N_COLLECT is not a real hub service flag — it piggybacks on CLICK_N_COLLECT.
		// An hub is a valid SNC hub when CLICK_N_COLLECT is enabled AND it has at least one active sncProduct.
		const hasClickNCollect = hub.meta.services?.includes("CLICK_N_COLLECT") ?? false;
		const hasActiveSncProducts = (hub.sncProducts?.totalCount ?? 0) > 0;
		return hasClickNCollect && hasActiveSncProducts;
	}

	if (filterService && !hub.meta.services?.includes(filterService)) {
		return false;
	}

	return true;
};

export const filterHubs = <THub extends FilterableHub>(hubs: readonly THub[], options: HubFilterOptions) => {
	return hubs.filter((hub) => matchesHubMapFilters(hub, options));
};

const HUB_ROUTE_FILTER_SERVICES: ReadonlyArray<[segment: string, service: HubFilterService]> = [
	["instant-buy", "INSTANT_BUY"],
	["shop", "SHOP_N_COLLECT"],
	["raffle", "RAFFLE"],
	["swap", "LOCK_B2B"],
	["lock", "LOCK_B2C"],
];

/** Service filter implied by a hub-scoped route such as `/harry/swap/my`. */
export const getHubRouteFilterService = (pathname: string, hubMetaName?: string | null): HubFilterService | null => {
	if (!hubMetaName) return null;
	const prefix = `/${hubMetaName}`;
	return HUB_ROUTE_FILTER_SERVICES.find(([segment]) => pathname.startsWith(`${prefix}/${segment}`))?.[1] ?? null;
};

export const getHubMapInitialSelection = <THub extends Pick<HubListItem, "id" | "meta">>(
	hubs: readonly THub[],
	currentHubMetaName?: string | null
) => {
	if (!currentHubMetaName) return null;
	const hub = hubs.find((candidate) => candidate.meta.name === currentHubMetaName);
	if (!hub) return null;

	return { city: hub.meta.city?.trim() || null, hubId: hub.id };
};

export const sortHubsByDistance = <THub extends Pick<HubListItem, "meta" | "status">>(
	hubs: readonly THub[],
	currentLocation?: Coordinates | null
): Array<THub & { distance?: number }> => {
	if (!currentLocation) {
		return hubs.map((hub) => ({ ...hub }));
	}

	const calcDistance = getDistanceGCS(currentLocation.latitude, currentLocation.longitude);
	const onlineHubs: Array<THub & { distance: number }> = [];
	const offlineHubs: Array<THub & { distance: number }> = [];

	for (const hub of hubs) {
		const distance = calcDistance(hub.meta.lat, hub.meta.lng);
		const nextHub = { ...hub, distance };

		if (hub.status === "ONLINE") {
			onlineHubs.push(nextHub);
			continue;
		}

		offlineHubs.push(nextHub);
	}

	return [...onlineHubs.sort((a, b) => a.distance - b.distance), ...offlineHubs.sort((a, b) => a.distance - b.distance)];
};
